// Mini modal with picture separated by a vertical line
import React, { useContext, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import { MobileDevicePreview } from '@bbkAdminComponents/preview-device/mobile-device';
import { type RestVideoRowModal } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestVideoRowModal;
  close: () => void;
  detailedAction: React.ReactElement;
};

export const VideoV2: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      info: { title, details },
      hero,
    },
    mobileLayout,
  },
  close,
  detailedAction,
}) => {
  const { mobileView, tabletView } = useMediaQueries();

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const videoRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-video`, videoRef), [videoRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const offerContext = useContext(EditOfferPageContext);
  const isPreview = !!offerContext;
  const isMobilePreview =
    isPreview && offerContext?.previewDevice === PreviewDevice.MOBILE;

  const content = (
    <ModalDesktopInnerSpacer
      css={css`
        display: grid;
        ${tabletView && 'max-width: 750px;'}
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${isMobilePreview && 'height: 100%;'}
      `}
      className={cs('video', 'display-grid', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={
          <span ref={titleRef}>
            <TitleTheme title={title} />
          </span>
        }
        close={close}
      />
      <div
        css={css`
          position: relative;
          padding-bottom: 56.25%;
          padding-top: 25px;
          height: 0;
          box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.15);
        `}
        ref={videoRef}
      >
        <iframe
          title="watch-video"
          className="video-embed"
          src={hero.url}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
          `}
        />
      </div>
      <div
        css={css`
          padding-top: 1rem;
        `}
        className={cs('display-grid', 'content-items', {
          desktop: !isMobilePreview && tabletView,
          mobile: mobileView || (isPreview && isMobilePreview),
        })}
      >
        <div
          css={css`
            padding-right: 1rem;
            overflow-y: auto;
          `}
          ref={descRef}
        >
          <CustomerHtml value={details} />
        </div>
        <div ref={actionsRef}>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );

  if (offerContext && isMobilePreview) {
    return (
      <MobileDevicePreview mobileLayout={mobileLayout}>
        {content}
      </MobileDevicePreview>
    );
  }

  return content;
};
